import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Sparkles, Brain, ChevronDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import { useAuth } from "@/hooks/useAuth";
import { useTutorial } from "@/hooks/useTutorial";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getTrialStatus, getFreeTierSearchStatus, FREE_TIER_SEARCH_LIMIT } from "@/lib/utils";
import { useThrottle } from "@/hooks/useDebounce";
interface Doctrina {
  id: number;
  texto: string;
}
interface SearchResult {
  fallo_id?: number;
  texto: string;
  metadata: {
    id: number;
    fallo_id: number;
    tribunal: string;
    expediente: string;
    caratula: string;
    fecha: string;
    link: string;
  };
  doctrinas: Doctrina[];
}
interface SearchInterfaceProps {
  onResults: (results: SearchResult[], collection: string) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onTabChange?: (tab: string) => void;
  onSearchTextChange?: (text: string) => void;
  onCollectionChange?: (collection: string) => void;
  onPaginationReset?: () => void;
  onFilterTermsChange?: (terms: string[]) => void;
}

interface SearchInterfaceHandle {
  searchDoctrina: (text: string, collection: string) => Promise<void>;
  searchObjeto: (text: string, collection: string) => Promise<void>;
  getActiveTab: () => string;
}
const CASE_LAW_COLLECTIONS = [
  {
    value: "supreme_court",
    label: "Supreme Court Cases",
  },
  {
    value: "federal_courts",
    label: "Federal Courts",
  },
  {
    value: "state_courts",
    label: "State Courts",
  },
  {
    value: "constitutional_law",
    label: "Constitutional Law",
  },
  {
    value: "criminal_law",
    label: "Criminal Law",
  },
  {
    value: "civil_rights",
    label: "Civil Rights",
  },
  {
    value: "commercial_law",
    label: "Commercial Law",
  },
  {
    value: "employment_law",
    label: "Employment Law",
  },
];
export const SearchInterface = forwardRef<SearchInterfaceHandle, SearchInterfaceProps>(
  (
    {
      onResults,
      onLoadingChange,
      onTabChange,
      onSearchTextChange,
      onCollectionChange,
      onPaginationReset,
      onFilterTermsChange,
    }: SearchInterfaceProps,
    ref,
  ) => {
    const { user, session, subscription } = useAuth();
    const { currentStep, completeStep } = useTutorial();
    const { checkThrottle, isThrottled } = useThrottle(500); // 500ms minimum between searches
    const [collection, setCollection] = useState<string>("");
    const [searchText, setSearchText] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [collections, setCollections] = useState<
      {
        value: string;
        label: string;
      }[]
    >(CASE_LAW_COLLECTIONS);
    const [activeTab, setActiveTab] = useState<string>("objetos");
    const [limit, setLimit] = useState<number>(25);
    const [page, setPage] = useState<number>(1);
    const [lastCount, setLastCount] = useState<number>(0);
    
    // Advanced filters state
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");
    const [positiveTerms, setPositiveTerms] = useState<string[]>([]);
    const [negativeTerms, setNegativeTerms] = useState<string[]>([]);
    const [positiveTermInput, setPositiveTermInput] = useState("");
    const [negativeTermInput, setNegativeTermInput] = useState("");
    
    // Notify parent when positive filter terms change
    useEffect(() => {
      onFilterTermsChange?.(positiveTerms);
    }, [positiveTerms, onFilterTermsChange]);

    useEffect(() => {
      const loadSchemas = async () => {
        try {
          const res = await fetch("https://v2.api.doctia.com.ar/schemas", {
            headers: session?.access_token
              ? {
                  Authorization: `Bearer ${session.access_token}`,
                }
              : undefined,
          });
          if (!res.ok) throw new Error("Failed to load schemas");
          const data = await res.json();
          if (Array.isArray(data?.schemas)) {
            const mapped = data.schemas.map((s: { name: string; description?: string }) => ({
              value: s.name,
              label: s.description || s.name,
            }));
            setCollections(mapped);
          }
        } catch (e) {
          console.error("Schemas load error:", e);
          toast.error("No se pudieron cargar las colecciones");
        }
      };
      loadSchemas();
    }, [session]);

    // Check if user can perform search (trial/limits validation)
    const checkSearchEligibility = (): boolean => {
      if (!user) {
        toast.error("Please sign in to perform searches");
        return false;
      }
      if (!session?.access_token) {
        toast.error("No hay token de acceso. Inicie sesión nuevamente.");
        return false;
      }

      // Check trial status for free users
      const isFree = !subscription?.subscribed || subscription?.plan === "free";

      if (isFree) {
        const { isTrialExpired } = getTrialStatus(subscription);
        const { isFreeExpiredTrial, limitReached, searchesRemaining } = getFreeTierSearchStatus(subscription);
        
        if (isTrialExpired) {
          // Check if user is in trial group with free tier searches
          if (isFreeExpiredTrial) {
            if (limitReached) {
              toast("Límite de búsquedas gratuitas alcanzado", {
                description: `Has usado tus ${FREE_TIER_SEARCH_LIMIT} búsquedas mensuales gratuitas. Suscribite para búsquedas ilimitadas.`,
              });
              return false;
            }
            // Allow search - will be deducted
            return true;
          }
          
          // Non-trial group or discount group - block completely
          toast("Período de prueba expirado", {
            description: "Tu período de prueba ha expirado. Suscribite para continuar buscando.",
          });
          return false;
        }
      }

      // Check Micro plan search limit
      if (subscription?.plan === "micro") {
        const searchesUsed = subscription?.monthly_searches_used ?? 0;
        if (searchesUsed >= 100) {
          toast("Límite de búsquedas alcanzado", {
            description:
              "Has alcanzado el límite de 100 búsquedas mensuales del plan Micro. Suscribite a Basic o Pro para búsquedas ilimitadas.",
          });
          return false;
        }
      }

      return true;
    };

    // Apply advanced filters to results (client-side filtering)
    const applyAdvancedFilters = (results: SearchResult[]): SearchResult[] => {
      let filtered = [...results];

      // Date filtering
      if (dateFrom || dateTo) {
        filtered = filtered.filter((result) => {
          const fecha = result.metadata?.fecha;
          if (!fecha) return false;
          
          const resultDate = new Date(fecha);
          if (isNaN(resultDate.getTime())) return false;

          if (dateFrom) {
            const fromDate = new Date(dateFrom);
            if (resultDate < fromDate) return false;
          }

          if (dateTo) {
            const toDate = new Date(dateTo);
            if (resultDate > toDate) return false;
          }

          return true;
        });
      }

      // Positive term filtering (must include at least one term)
      if (positiveTerms.length > 0) {
        filtered = filtered.filter((result) => {
          const textoLower = result.texto?.toLowerCase() || "";
          const doctrinasText = result.doctrinas?.map(d => d.texto?.toLowerCase() || "").join(" ");
          const allText = textoLower + " " + doctrinasText;

          return positiveTerms.some(term => allText.includes(term.toLowerCase()));
        });
      }

      // Negative term filtering (exclude any result with these terms)
      if (negativeTerms.length > 0) {
        filtered = filtered.filter((result) => {
          const textoLower = result.texto?.toLowerCase() || "";
          const doctrinasText = result.doctrinas?.map(d => d.texto?.toLowerCase() || "").join(" ");
          const allText = textoLower + " " + doctrinasText;

          return !negativeTerms.some(term => allText.includes(term.toLowerCase()));
        });
      }

      return filtered;
    };

    const handleSearch = async () => {
      if (!collection || !searchText.trim()) {
        toast.error("Please select a collection and enter search text");
        return;
      }

      // Client-side throttle to prevent rapid-fire searches
      if (!checkThrottle()) {
        toast.info("Por favor espera un momento entre búsquedas");
        return;
      }

      if (!checkSearchEligibility()) {
        return;
      }

      setIsSearching(true);
      onLoadingChange?.(true);
      try {
        const currentPage = page; // default to current state

        const response = await fetch("https://v2.api.doctia.com.ar/search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: searchText,
            collection_name: collection,
            search_in: activeTab,
            limit: limit,
          }),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Search failed");
        }
        const data = await response.json();
        const rawResults = Array.isArray(data?.results) ? data.results : [];
        const mappedResults: SearchResult[] = rawResults;

        // Apply advanced filters
        const filteredResults = applyAdvancedFilters(mappedResults);

        // Get friendly collection name for storage
        const friendlyCollectionName = collections.find((c) => c.value === collection)?.label || collection;

        // Save search to history (save raw results, not filtered)
        await supabase.from("search_history").insert({
          user_id: user.id,
          collection_name: friendlyCollectionName,
          search_text: searchText,
          results: mappedResults as any,
        });


        setLastCount(filteredResults.length);
        onResults(filteredResults, collection);
        
        // Check if this is a tutorial search
        const queryLower = searchText.toLowerCase();
        const collectionLower = collection.toLowerCase();
        
        // Step 1: First search - "responsabilidad civil" in "cámara nacional civil"
        if (currentStep === "first_search" && 
            queryLower.includes("responsabilidad civil") && 
            collectionLower.includes("civil")) {
          completeStep("first_search");
        }
        
      // Step 2: Second search - "indexar" + "laboral" in "trabajo"
      if (currentStep === "second_search" && 
          queryLower.includes("indexar") && 
          queryLower.includes("laboral") && 
          collectionLower.includes("trabajo")) {
        completeStep("second_search");
      }
        
        // Show toast with filter info
        const hasFilters = dateFrom || dateTo || positiveTerms.length > 0 || negativeTerms.length > 0;
        if (hasFilters) {
          toast.success(`Se encontraron ${filteredResults.length} resultados (${mappedResults.length} antes de filtros)`);
        } else {
          toast.success(`Se encontraron ${filteredResults.length} resultados`);
        }

        // After search completes, scroll to results area
        setTimeout(() => {
          const resultsSection = document.querySelector("[data-results-section]");
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 300);
      } catch (error) {
        console.error("Search error:", error);
        toast.error("No se pudo completar la búsqueda");
      } finally {
        setIsSearching(false);
        onLoadingChange?.(false);
      }
    };

    const searchDoctrina = async (text: string, currentCollection: string) => {
      // Redirect to objeto search for backward compatibility
      return searchObjeto(text, currentCollection);
    };

    const searchObjeto = async (text: string, currentCollection: string) => {
      // First scroll to search box area
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Wait for scroll to complete before proceeding
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Switch to objetos tab
      setActiveTab("objetos");
      onTabChange?.("objetos");

      // Set search text and collection
      setSearchText(text);
      setCollection(currentCollection);
      onSearchTextChange?.(text);
      onCollectionChange?.(currentCollection);

      // Reset pagination
      setPage(1);
      onPaginationReset?.();

      // Perform the search
      await performSearch(text, currentCollection, "objetos");
    };

    const performSearch = async (text: string, currentCollection: string, searchIn: string) => {
      if (!currentCollection || !text.trim()) {
        toast.error("Please select a collection and enter search text");
        return;
      }

      // Client-side throttle to prevent rapid-fire searches
      if (!checkThrottle()) {
        toast.info("Por favor espera un momento entre búsquedas");
        return;
      }

      // Check search eligibility (trial status, limits, etc.)
      if (!checkSearchEligibility()) {
        return;
      }

      setIsSearching(true);
      onLoadingChange?.(true);
      try {
        const response = await fetch("https://v2.api.doctia.com.ar/search", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: text,
            collection_name: currentCollection,
            search_in: searchIn,
            limit: limit,
          }),
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || "Search failed");
        }
        const data = await response.json();
        const rawResults = Array.isArray(data?.results) ? data.results : [];
        const mappedResults: SearchResult[] = rawResults;

        // Apply advanced filters
        const filteredResults = applyAdvancedFilters(mappedResults);

        // Get friendly collection name for storage
        const friendlyCollectionName =
          collections.find((c) => c.value === currentCollection)?.label || currentCollection;

        // Save search to history (save raw results, not filtered)
        await supabase.from("search_history").insert({
          user_id: user.id,
          collection_name: friendlyCollectionName,
          search_text: text,
          results: mappedResults as any,
        });


        setLastCount(filteredResults.length);
        onResults(filteredResults, currentCollection);
        
        // Show toast with filter info
        const hasFilters = dateFrom || dateTo || positiveTerms.length > 0 || negativeTerms.length > 0;
        if (hasFilters) {
          toast.success(`Se encontraron ${filteredResults.length} resultados (${mappedResults.length} antes de filtros)`);
        } else {
          toast.success(`Se encontraron ${filteredResults.length} resultados`);
        }

        // After search completes, scroll to results area
        setTimeout(() => {
          const resultsSection = document.querySelector("[data-results-section]");
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 300);
      } catch (error) {
        console.error("Search error:", error);
        toast.error("No se pudo completar la búsqueda");
      } finally {
        setIsSearching(false);
        onLoadingChange?.(false);
      }
    };

    // Expose the search functions via ref
    useImperativeHandle(ref, () => ({
      searchDoctrina,
      searchObjeto,
      getActiveTab: () => activeTab,
    }));

    // Get trial status for free users
    const isFree = !subscription?.subscribed;
    const { isTrialActive, isTrialExpired, daysLeft } = getTrialStatus(subscription);
    const { isFreeExpiredTrial, searchesRemaining, limitReached } = getFreeTierSearchStatus(subscription);

    return (
      <Card className="shadow-card border-border bg-card hover:shadow-card-hover transition-shadow duration-300 relative overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 mb-2">
            <CardTitle className="flex items-center gap-4 text-3xl text-foreground">
              <div className="p-3 bg-primary rounded-xl">
                <Brain className="h-8 w-8 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-foreground">
                  Investigación Legal
                </span>
                <span className="text-sm text-muted-foreground font-normal">
                  Potenciada por Inteligencia Artificial
                </span>
              </div>
            </CardTitle>

            {/* Trial Status Indicator */}
            {isFree && isTrialActive && (
              <div className="flex-shrink-0">
                <div
                  className={`px-4 py-2 rounded-lg border ${
                    daysLeft <= 3
                      ? "bg-warning/10 border-warning/30"
                      : "bg-primary/10 border-primary/30"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`text-2xl ${daysLeft <= 3 ? "animate-pulse" : ""}`}>
                      {daysLeft <= 3 ? "⚠️" : "🎁"}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Prueba Gratis
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          daysLeft <= 3 ? "text-warning" : "text-primary"
                        }`}
                      >
                        {daysLeft} día{daysLeft !== 1 ? "s" : ""} restante{daysLeft !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFree && isTrialExpired && isFreeExpiredTrial && (
              <div className="flex-shrink-0">
                <div className={`px-4 py-2 rounded-lg border ${
                  limitReached 
                    ? "bg-destructive/10 border-destructive/30"
                    : "bg-warning/10 border-warning/30"
                }`}>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{limitReached ? "🔒" : "🔍"}</div>
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Búsquedas Gratuitas
                      </span>
                      <span className={`text-sm font-bold ${
                        limitReached 
                          ? "text-destructive"
                          : "text-warning"
                      }`}>
                        {limitReached 
                          ? "Límite alcanzado" 
                          : `${searchesRemaining} de ${FREE_TIER_SEARCH_LIMIT} restantes`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFree && isTrialExpired && !isFreeExpiredTrial && (
              <div className="flex-shrink-0">
                <div className="px-4 py-2 rounded-lg border bg-destructive/10 border-destructive/30">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">🔒</div>
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Prueba Expirada
                      </span>
                      <span className="text-sm font-bold text-destructive">
                        Suscribite para continuar
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-8 relative z-10">
          <div className="space-y-8">
            {/* Objetos Search - Now the only search mode */}
            {/* AI Search Input */}
            <div className="space-y-4">
              <Label
                htmlFor="query-objetos"
                className="text-base font-semibold text-foreground flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4 text-primary-glow" />
                Buscá por objeto procesal, cuestiones de hecho o derecho, o tema del caso
              </Label>
              <div className="relative group">
                <textarea
                  id="query-objetos"
                  placeholder="Describí tu consulta en lenguaje natural. Cuanto más contexto, mejor. Ej: 'Responsabilidad civil de las clínicas de fertilidad por falta de indicación de diagnóstico genético'"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value.slice(0, 2200))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !isSearching && collection && searchText.trim()) {
                      e.preventDefault();
                      setPage(1);
                      handleSearch();
                    }
                  }}
                  rows={3}
                  maxLength={2200}
                  className="w-full min-h-[120px] text-lg px-6 py-4 pr-16 border-2 border-primary/20 bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-lg hover:border-primary/40 focus:border-primary focus:shadow-[0_0_30px_hsl(var(--primary)/0.4)] focus:shadow-primary/20 transition-all duration-500 rounded-xl group-hover:shadow-lg placeholder:text-muted-foreground/70 resize-none outline-none"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-all duration-300">
                    <Brain className="h-5 w-5 text-primary-glow animate-pulse" />
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-xl pointer-events-none"></div>
              </div>
            </div>

            {/* Collection and Limit Selection in same row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 space-y-4">
                <Label htmlFor="collection-objetos" className="text-sm font-medium text-muted-foreground">
                  Colección
                </Label>
                <Select
                  value={collection}
                  onValueChange={(v) => {
                    setCollection(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-12 border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 hover:bg-card/70 transition-all duration-300 focus:border-primary focus:shadow-glow rounded-xl">
                    <SelectValue placeholder="Seleccione una colección..." />
                  </SelectTrigger>
                  <SelectContent className="bg-popover/95 backdrop-blur-xl border-border/30 shadow-intense z-50 rounded-xl">
                    {collections.map((item) => (
                      <SelectItem
                        key={item.value}
                        value={item.value}
                        className="hover:bg-primary/10 transition-colors duration-200 rounded-lg"
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 space-y-4">
                <Label htmlFor="limit-objetos" className="text-sm font-medium text-muted-foreground">
                  Cantidad máxima de resultados
                </Label>
                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-12 border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 hover:bg-card/70 transition-all duration-300 focus:border-primary focus:shadow-glow rounded-xl">
                    <SelectValue placeholder="Seleccione el límite" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover/95 backdrop-blur-xl border-border/30 shadow-intense z-50 rounded-xl">
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem
                        key={n}
                        value={String(n)}
                        className="hover:bg-primary/10 transition-colors duration-200 rounded-lg"
                      >
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <Collapsible open={advancedFiltersOpen} onOpenChange={setAdvancedFiltersOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between h-12 border border-primary/20 bg-gradient-to-r from-card/60 to-card/40 backdrop-blur-sm hover:border-primary/40 hover:bg-gradient-to-r hover:from-card/80 hover:to-card/60 transition-all duration-300 rounded-xl text-foreground group"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-primary-glow" />
                  Filtros avanzados
                  {(dateFrom || dateTo || positiveTerms.length > 0 || negativeTerms.length > 0) && (
                    <Badge variant="secondary" className="ml-2 bg-primary/20 text-primary-foreground border-primary/30">
                      {[dateFrom, dateTo, ...positiveTerms, ...negativeTerms].filter(Boolean).length}
                    </Badge>
                  )}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground group-hover:text-primary transition-all duration-300 ${advancedFiltersOpen ? "rotate-180" : ""}`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="space-y-5 p-5 border border-primary/10 rounded-xl bg-gradient-to-br from-card/40 via-card/30 to-card/20 backdrop-blur-lg shadow-lg">
                {/* Date Range Filter */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                    📅 Filtrar por fecha
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="date-from" className="text-xs text-muted-foreground">
                        Fecha desde
                      </Label>
                      <Input
                        id="date-from"
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-11 border-primary/20 bg-background/80 backdrop-blur-sm hover:border-primary/40 focus:border-primary focus:shadow-glow transition-all duration-300 rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date-to" className="text-xs text-muted-foreground">
                        Fecha hasta
                      </Label>
                      <Input
                        id="date-to"
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-11 border-primary/20 bg-background/80 backdrop-blur-sm hover:border-primary/40 focus:border-primary focus:shadow-glow transition-all duration-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Terms Filters - Side by side on larger screens */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Positive Terms Filter */}
                  <div className="space-y-3">
                    <Label htmlFor="positive-terms" className="text-sm font-semibold text-foreground flex items-center gap-2">
                      ✅ Incluir términos
                    </Label>
                    <Input
                      id="positive-terms"
                      type="text"
                      placeholder="responsabilidad, clínica..."
                      value={positiveTermInput}
                      onChange={(e) => setPositiveTermInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const terms = positiveTermInput
                            .split(",")
                            .map((t) => t.trim())
                            .filter((t) => t && !positiveTerms.includes(t));
                          if (terms.length > 0) {
                            setPositiveTerms([...positiveTerms, ...terms]);
                            setPositiveTermInput("");
                          }
                        }
                      }}
                      onBlur={() => {
                        if (positiveTermInput.trim()) {
                          const terms = positiveTermInput
                            .split(",")
                            .map((t) => t.trim())
                            .filter((t) => t && !positiveTerms.includes(t));
                          if (terms.length > 0) {
                            setPositiveTerms([...positiveTerms, ...terms]);
                            setPositiveTermInput("");
                          }
                        }
                      }}
                      className="h-11 border-primary/20 bg-background/80 backdrop-blur-sm hover:border-primary/40 focus:border-primary focus:shadow-glow transition-all duration-300 rounded-lg placeholder:text-muted-foreground/60"
                    />
                    <p className="text-xs text-muted-foreground">Separados por comas</p>
                    {positiveTerms.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
                        {positiveTerms.map((term) => (
                          <Badge key={term} variant="default" className="gap-1.5 pl-3 pr-2 py-1.5 bg-gradient-primary shadow-sm hover:shadow-glow transition-all duration-300">
                            <span className="text-xs font-medium">{term}</span>
                            <button
                              onClick={() => setPositiveTerms(positiveTerms.filter((t) => t !== term))}
                              className="hover:bg-primary-foreground/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Negative Terms Filter */}
                  <div className="space-y-3">
                    <Label htmlFor="negative-terms" className="text-sm font-semibold text-foreground flex items-center gap-2">
                      ⛔ Excluir términos
                    </Label>
                    <Input
                      id="negative-terms"
                      type="text"
                      placeholder="penal, laboral..."
                      value={negativeTermInput}
                      onChange={(e) => setNegativeTermInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const terms = negativeTermInput
                            .split(",")
                            .map((t) => t.trim())
                            .filter((t) => t && !negativeTerms.includes(t));
                          if (terms.length > 0) {
                            setNegativeTerms([...negativeTerms, ...terms]);
                            setNegativeTermInput("");
                          }
                        }
                      }}
                      onBlur={() => {
                        if (negativeTermInput.trim()) {
                          const terms = negativeTermInput
                            .split(",")
                            .map((t) => t.trim())
                            .filter((t) => t && !negativeTerms.includes(t));
                          if (terms.length > 0) {
                            setNegativeTerms([...negativeTerms, ...terms]);
                            setNegativeTermInput("");
                          }
                        }
                      }}
                      className="h-11 border-destructive/20 bg-background/80 backdrop-blur-sm hover:border-destructive/40 focus:border-destructive focus:shadow-[0_0_15px_hsl(var(--destructive)/0.3)] transition-all duration-300 rounded-lg placeholder:text-muted-foreground/60"
                    />
                    <p className="text-xs text-muted-foreground">Separados por comas</p>
                    {negativeTerms.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-destructive/5 rounded-lg border border-destructive/10">
                        {negativeTerms.map((term) => (
                          <Badge key={term} variant="destructive" className="gap-1.5 pl-3 pr-2 py-1.5 shadow-sm hover:shadow-[0_0_15px_hsl(var(--destructive)/0.3)] transition-all duration-300">
                            <span className="text-xs font-medium">{term}</span>
                            <button
                              onClick={() => setNegativeTerms(negativeTerms.filter((t) => t !== term))}
                              className="hover:bg-destructive-foreground/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Clear Filters Button */}
                {(dateFrom || dateTo || positiveTerms.length > 0 || negativeTerms.length > 0) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                      setPositiveTerms([]);
                      setNegativeTerms([]);
                      setPositiveTermInput("");
                      setNegativeTermInput("");
                    }}
                    className="w-full h-10 border border-border/50 hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive transition-all duration-300 rounded-lg font-medium"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Limpiar todos los filtros
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* AI Search Button */}
          <Button
            onClick={() => {
              setPage(1);
              handleSearch();
            }}
            disabled={isSearching || !collection || !searchText.trim()}
            size="lg"
            className="w-full h-14 bg-gradient-primary hover:shadow-glow text-primary-foreground shadow-intense hover:scale-[1.02] transition-all duration-500 border-0 relative overflow-hidden group disabled:opacity-50 disabled:hover:scale-100 rounded-xl text-lg font-semibold"
          >
            <span className="relative z-10 flex items-center gap-3">
              {isSearching ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Analizando con IA...
                </>
              ) : (
                <>
                  Buscar con IA
                  <Sparkles className="h-4 w-4 opacity-80" />
                </>
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </Button>
        </CardContent>
      </Card>
    );
  },
);
